import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Inbox as InboxIcon,
  FileText,
  Send,
  FolderMinus,
  Trash2,
  Archive,
  Search,
  Trash2 as TrashIcon,
  Archive as ArchiveIcon,
  Reply,
  CornerUpRight,
  MoreVertical,
} from 'lucide-react';

interface EmailItem {
  id: number;
  from: string;
  subject: string;
  date: string;
  preview: string;
  body: string;
  unread?: boolean;
}

const mockEmails: EmailItem[] = [
  {
    id: 1,
    from: 'alice@example.com',
    subject: 'Welcome to SuperTask Mail',
    date: 'Aug 4',
    preview: 'Hey there! Just wanted to welcome you to the new mailbox section…',
    body: `Hi there,\n\nWelcome to the brand new Email section of SuperTask. This is a simple wireframe that will evolve into a fully-featured webmail client.\n\nCheers,\nThe Team`,
    unread: true,
  },
  {
    id: 2,
    from: 'bob@example.com',
    subject: 'Project Update',
    date: 'Aug 3',
    preview: 'I have some updates on the project we discussed last week…',
    body: `Hello,\n\nHere is the latest status update on the Project. We have completed the initial design phase and are moving on to development.\n\nRegards,\nBob`,
    unread: false,
  },
];

const EmailSection: React.FC = () => {
  const { accountId } = useParams();
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selected, setSelected] = useState<EmailItem | null>(mockEmails[0]);

  // toolbar state
  const [filterMode, setFilterMode] = useState<'all' | 'unread'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const emailAccounts = [
    {
      id: 'personal',
      label: 'Personal',
      folders: [
        { id: 'inbox', label: 'Inbox', icon: <InboxIcon size={16} />, count: 128 },
        { id: 'drafts', label: 'Drafts', icon: <FileText size={16} />, count: 9 },
        { id: 'sent', label: 'Sent', icon: <Send size={16} /> },
        { id: 'junk', label: 'Junk', icon: <FolderMinus size={16} />, count: 23 },
        { id: 'trash', label: 'Trash', icon: <Trash2 size={16} /> },
        { id: 'archive', label: 'Archive', icon: <Archive size={16} /> },
        { id: 'divider', label: '', icon: null },
        { id: 'newsletters', label: 'Newsletters', icon: <FolderMinus size={16} />, count: 972 },
      ],
    },
    {
      id: 'work',
      label: 'Work',
      folders: [
        { id: 'inbox', label: 'Inbox', icon: <InboxIcon size={16} /> },
        { id: 'sent', label: 'Sent', icon: <Send size={16} /> },
      ],
    },
  ];

  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({ personal: true });
  const [selectedAccount, setSelectedAccount] = useState('personal');

  const currentAccount = emailAccounts.find(a=>a.id===selectedAccount) ?? emailAccounts[0];
  const folders = currentAccount.folders;

  const filteredEmails = mockEmails.filter(e => {
    if (filterMode === 'unread' && !e.unread) return false;
    if (searchTerm && !(`${e.from} ${e.subject} ${e.preview}`.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    return true;
  });

  return (
    <div className="flex h-[calc(100vh-96px)] bg-background">
      {/* Folders & Accounts */}
      <div className="w-56 border-r overflow-y-auto p-4 space-y-1">
        {emailAccounts.map(acc => (
          <React.Fragment key={acc.id}>
            {/* account row */}
            <div className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm font-medium">
              <button
                onClick={() => { setSelectedAccount(acc.id); }}
                className="flex-1 text-left"
              >
                {acc.label}
              </button>
              <button
                onClick={() => setExpandedAccounts(prev => ({ ...prev, [acc.id]: !prev[acc.id] }))}
                className="w-4 text-right"
              >
                {expandedAccounts[acc.id] ? 'v' : '>'}
              </button>
            </div>
            {/* folders */}
            {expandedAccounts[acc.id] && acc.folders.map(f => (
              f.id === 'divider' ? (
                <hr key={`div-${acc.id}`} className="my-2 border-muted ml-6" />
              ) : (
                <button
                  key={`${acc.id}-${f.id}`}
                  onClick={() => { setSelectedAccount(acc.id); setSelectedFolder(f.id); }}
                  className={`w-full flex items-center gap-2 pl-8 pr-2 py-1 rounded hover:bg-muted text-sm ${
                    selectedAccount === acc.id && selectedFolder===f.id ? 'bg-muted font-medium' : ''
                  }`}
                >
                  {f.icon}
                  <span className="flex-1 text-left">{f.label}</span>
                  {f.count !== undefined && <span className="opacity-60 text-xs">{f.count}</span>}
                </button>
              )
            ))}
          </React.Fragment>
        ))}
        {/* add account link */}
        <button
          className="mt-4 w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm"
          onClick={() => window.location.href = '/email/add'}
        >
          <span className="text-primary">＋</span>
          <span>Add Account</span>
        </button>
      </div>

      {/* Email list */}
      <div className="w-80 border-r overflow-y-auto">
        {/* toolbar */}
        <div className="sticky top-0 bg-background p-4 border-b space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold capitalize">{folders.find(f=>f.id===selectedFolder)?.label}</h2>
            {/* simple chips */}
            <div className="flex gap-2">
              <button
                className={`px-2 py-0.5 rounded border text-xs ${filterMode==='all'?'bg-primary text-white':'hover:bg-muted'}`}
                onClick={()=>setFilterMode('all')}
              >
                All mail
              </button>
              <button
                className={`px-2 py-0.5 rounded border text-xs ${filterMode==='unread'?'bg-primary text-white':'hover:bg-muted'}`}
                onClick={()=>setFilterMode('unread')}
              >
                Unread
              </button>
            </div>
          </div>
          {/* search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <input
              type="text"
              value={searchTerm}
              onChange={e=>setSearchTerm(e.target.value)}
              placeholder="Search"
              className="pl-8 pr-2 py-1 w-full border rounded text-sm"
            />
          </div>
        </div>

        <div className="p-4 space-y-2">
          {filteredEmails.map((email) => (
            <div
              key={email.id}
              className={`cursor-pointer rounded-md border p-2 hover:bg-muted transition-colors ${
                selected?.id === email.id ? 'bg-muted' : ''
              }`}
              onClick={() => setSelected(email)}
            >
              <div className="flex justify-between text-sm font-medium">
                <span>{email.from}</span>
                <span className="opacity-60">{email.date}</span>
              </div>
              <div className="font-semibold truncate">{email.subject}</div>
              <div className="text-sm opacity-70 truncate">{email.preview}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reading pane */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selected ? (
          <>
            {/* header */}
            <div className="flex items-center justify-between border-b p-4">
              <div className="flex items-center gap-2">
                <button className="p-1 hover:bg-muted rounded"><TrashIcon size={16}/></button>
                <button className="p-1 hover:bg-muted rounded"><ArchiveIcon size={16}/></button>
                <button className="p-1 hover:bg-muted rounded"><Reply size={16}/></button>
                <button className="p-1 hover:bg-muted rounded"><CornerUpRight size={16}/></button>
              </div>
              <span className="opacity-60 text-xs whitespace-nowrap">{selected.date}</span>
              <button className="p-1 hover:bg-muted rounded"><MoreVertical size={16}/></button>
            </div>

            {/* body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold">{selected.subject}</h2>
                <p className="text-sm opacity-70">
                  From: {selected.from}
                </p>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm">
                {selected.body}
              </pre>
            </div>

            {/* quick reply */}
            <div className="border-t p-4 space-y-3 flex flex-col basis-1/2 shrink-0">
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-primary" />
                  Mute this thread
                </label>
                <button className="px-4 py-1 bg-primary text-white rounded text-sm">Send</button>
              </div>
              <textarea
                className="flex-1 border rounded p-2 text-sm resize-none"
                placeholder={`Reply ${selected.from.split('@')[0]}...`}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Select an email to read
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSection; 